import { createSelector } from 'reselect';
import {
  AutoMaxContributionOptions,
  IRAAutoMaxCalculationData,
  IRAAutoMaxCalculationState,
} from '.';
import { ApplicationState } from '../../types';
import { getSelectedIRAAutoMaxFunds } from 'src/redux/modules/shoppingCart/shoppingCart.selectors';
import {
  getIRAAutoMaxCartDetailsRaw,
  getMonthsDaysCartesianProduct,
  getRemainingContributionDatesSorted,
} from '../IRAAutoMaxCartDetails/IRAAutoMaxCartDetails.selectors';
import { GetCalculationPayload } from '.';
import { IRAAutoMaxCartDetailsState } from '../IRAAutoMaxCartDetails';
import { getMonthName } from '../../../../utils/dates/date-utility';
/**
 *
 * @todo this function probs already exists
 * use existing implementation or move to utils
 */
export const fmtDate = (dateStr) => {
  const tDate = new Date(dateStr);
  const m = tDate.getMonth() + 1;
  const d = tDate.getDate();
  const y = tDate.getFullYear();
  return `${m}/${d}/${y}`;
};

export const getIRAAutoMaxCalculationRaw = ({
  iraAutoMaxCalculation,
}: ApplicationState): IRAAutoMaxCalculationState => {
  return iraAutoMaxCalculation;
};

export const getIRAAutoMaxCalculationData = createSelector(
  [getIRAAutoMaxCalculationRaw],
  (iraAutoMaxCalculationRaw): IRAAutoMaxCalculationData =>
    iraAutoMaxCalculationRaw.data
);

export const getIRAAutoMaxCalculationLoaded = createSelector(
  [getIRAAutoMaxCalculationRaw],
  (iraAutoMaxCalculationRaw) => iraAutoMaxCalculationRaw.isLoaded
);

// pull first amount off last contribution
export const getContributionPerAccount = createSelector(
  [getIRAAutoMaxCalculationData],
  ({ contributions }) => {
    return contributions?.[contributions.length - 1]?.amount || 0;
  }
);

// changed because it was printing wrong month
// now calls function from buy flow
export const toMonthName = (num: number): string => {
  return getMonthName(getMonthName(num).toUpperCase());
};

export const getNumberWithOrdinal = (n) => {
  const s = ['th', 'st', 'nd', 'rd'],
    v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

export const getFrequency = createSelector(
  [getMonthsDaysCartesianProduct],
  (remainingContributionDates) => {
    const uniqueMonths: Set<number> = new Set(
      remainingContributionDates.map(([m]) => m).sort((a, b) => a - b)
    );
    const uniqueDays: Set<number> = new Set(
      remainingContributionDates.map(([, d]) => d).sort((a, b) => a - b)
    );
    const months = Array.from(new Set(uniqueMonths))
      .map(toMonthName)
      .join(', ');
    const uniqueDaysArr = Array.from(new Set(uniqueDays)).map(
      getNumberWithOrdinal
    );
    const days = [
      ...uniqueDaysArr.slice(0, -2),
      uniqueDaysArr.slice(-2).join(' and '),
    ].join(', ');
    return `${months} on the ${days}`;
  }
);

export const getAccountsTo = createSelector(
  [getSelectedIRAAutoMaxFunds],
  (selectedIRAAutoMaxFunds) => {
    if (!Array.isArray(selectedIRAAutoMaxFunds)) {
      throw new TypeError(
        'was expecting selectedIRAAutoMaxFunds to be an array'
      );
    }
    return selectedIRAAutoMaxFunds.map((f) => ({
      accountType: f.agreementType.replace(/_/g, ' '),
      fundName: f.fundName,
      ticker: f.ticker,
      agreementNumber: f.agreementNumber,
      name: f.formattedEntityNames,
    }));
  }
);

const toDate = ([m, d]) => {
  const month = getMonthName(getMonthName(m).toUpperCase());
  return `${month} ${d}`;
};

export const getStartAndMaximizeByDate = createSelector(
  getRemainingContributionDatesSorted,
  getMonthsDaysCartesianProduct,
  (sortedDates, cartesianProduct) => {
    if (!sortedDates.length) {
      return [];
    }
    const cartesianProductSorted = cartesianProduct.sort((a, b) => {
      if (a[0] === b[0]) {
        return a[1] - b[1];
      } else {
        return a[0] - b[0];
      }
    });
    const [firstDateTuple] = cartesianProductSorted;
    const lastDateTuple =
      cartesianProductSorted[cartesianProductSorted.length - 1];
    return [toDate(firstDateTuple), toDate(lastDateTuple)];
  }
);

export const getAutomaxContributionOptions = createSelector(
  [getIRAAutoMaxCartDetailsRaw, getSelectedIRAAutoMaxFunds],
  (
    iraAutoMaxCartDetailsRaw: IRAAutoMaxCartDetailsState,
    selectedIRAAutoMaxFunds
  ): AutoMaxContributionOptions => {
    if (!Array.isArray(selectedIRAAutoMaxFunds)) {
      throw new TypeError(
        'was expecting selectedIRAAutoMaxFunds to be an array'
      );
    }
    const { monthsForPayments, daysOfMonthPayments } = iraAutoMaxCartDetailsRaw;
    return {
      monthsForPayments: monthsForPayments
        .map((m) => m.value)
        .sort((a, b) => a - b),
      daysOfMonthPayments: daysOfMonthPayments
        .map((d) => d.value)
        .sort((a, b) => a - b),
      agreementKeys: selectedIRAAutoMaxFunds.map((f) => ({
        '@Type': 'AgreementKey',
        agreementID: f.agreementID,
      })),
    };
  }
);

export const getCalculationPayload = createSelector(
  [getAutomaxContributionOptions],
  (
    autoMaxContributionOptions: AutoMaxContributionOptions
  ): GetCalculationPayload => ({ autoMaxContributionOptions })
);

// sum amounts of raw contributions
export const getTotalContributionPerPeriod = createSelector(
  [getContributionPerAccount, getAccountsTo],
  (amountPerPeriod, accountsTo) => {
    return amountPerPeriod * accountsTo?.length || 0;
  }
);

export const getIRAAutoMaxCalculationResponse = createSelector(
  [getIRAAutoMaxCalculationRaw],
  (calculation) => calculation
);

export const getIRAAutoMaxCalculationDataSorted = createSelector(
  [getIRAAutoMaxCalculationData, getAccountsTo],
  (calculateDataRaw, toAccounts) => {
    const temp = toAccounts.map((account) => {
      const contributions = calculateDataRaw.contributions.filter(
        (contribution) =>
          account.agreementNumber ===
          `${contribution.agreementKey.agreementNumber}-${contribution.agreementKey.checkSum}`
      );
      return {
        contributions,
        ...account,
      };
    });
    return temp;
  }
);

// format based off of first contribution
export const getStartDate = createSelector(
  [getIRAAutoMaxCalculationDataSorted],
  (contributionsRaw) => {
    const contributions = contributionsRaw[0]?.contributions.filter(
      (transaction) => transaction?.amount > 0
    );
    if (contributions.length === 0) {
      return '';
    }
    const date = contributions[0]?.withdrawalDate;
    const temp = date?.split('T')[0]?.split('-');
    return `${parseInt(temp[1])}/${parseInt(temp[2])}/${temp[0]}`;
  }
);
